import React from 'react';
import { Box, IconButton, Avatar } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { IAccount } from '../Interfaces/IAccount';
import { checkPermission, getCurrentUserId } from "../utils/permission";
import { useAuthenticatedImage } from '../hooks/useAuthenticatedImage';

interface EditableProfilePictureProps {
    account: IAccount;
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AVATAR_SIZE = 112;

const EditableProfilePicture: React.FC<EditableProfilePictureProps> = ({ account, onFileChange }) => {
    const { profile_picture, first_name, last_name, id, avatar_url, has_avatar } = account;
    // profile_picture ist nur gesetzt, wenn gerade ein NEUES Bild gewählt wurde (base64-Vorschau).
    const freshBase64 = profile_picture?.trim() || '';
    // Bestehendes Bild lazy per URL laden (nur wenn kein neues ausgewählt ist) – kein base64 mehr aus der Liste.
    const existingAvatarUrl = freshBase64
        ? null
        : (avatar_url || (has_avatar ? `/api/users/${id}/avatar` : null));
    const { src: existingSrc } = useAuthenticatedImage(existingAvatarUrl);
    const imageSrc = freshBase64 ? `data:image/*;base64,${freshBase64}` : existingSrc;
    const hasImage = Boolean(imageSrc);
    const canEdit = checkPermission(1, 2) || id === getCurrentUserId();

    return (
        <Box
            sx={{
                position: 'relative',
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
            }}
        >
            {/* Runder Clip-Container für das Bild, damit der Edit-Button frei darüber liegen kann */}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #ffffff',
                    boxShadow: '0px 12px 24px rgba(15, 23, 42, 0.12)',
                    transition: 'transform 200ms ease, box-shadow 200ms ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0px 16px 34px rgba(15, 23, 42, 0.16)',
                    },
                }}
            >
                {hasImage ? (
                    // Das echte Bild
                    <Box
                        component="img"
                        src={imageSrc}
                        alt={`${first_name || ''} ${last_name || ''}`}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    // Fallback-Avatar
                    <Avatar sx={{ width: '100%', height: '100%' }}>
                        {`${first_name?.[0] || ''}${last_name?.[0] || ''}`}
                    </Avatar>
                )}
            </Box>

            {canEdit && (
                <IconButton
                    component="label"
                    sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        bgcolor: 'background.paper',
                        color: 'var(--pvta-green-800)',
                        borderRadius: '50%',
                        border: '2px solid #ffffff',
                        p: 0.6,
                        boxShadow: '0px 6px 14px rgba(20, 35, 25, 0.20)',
                        transition: 'transform 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
                        '&:hover': {
                            bgcolor: 'var(--pvta-green-100)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0px 10px 18px rgba(20, 35, 25, 0.24)',
                        },
                    }}
                >
                    <EditIcon fontSize="small" />
                    <input type="file" accept="image/*" hidden onChange={onFileChange} />
                </IconButton>
            )}
        </Box>
    );
};

export default EditableProfilePicture;
